<template>
  <div class="PagePatientDashboard">
    <v-btn text small @click="$router.push('/patients')" class="mb-2">
      <v-icon left>mdi-arrow-left</v-icon> Voltar para pacientes
    </v-btn>

    <dependent-card v-if="patient" :dependent="patient" :showButtons="false" />
    <br />

    <v-btn block color="secondary" :loading="exporting" @click="exportPdf" class="mb-4">
      <v-icon left>mdi-file-pdf-box</v-icon> Exportar Relatório PDF
    </v-btn>

    <div id="pdf-content">
      <spasms-chart v-model="selectedDays" :metrics="metrics" :annotations="chartAnnotations" />
      <br />
      <pro-gate description="Indicadores por turno, horários de pico e crises por tipo estão disponíveis no Plano Pro.">
        <spasms-indicators
          :days="selectedDays"
          :period-metrics="periodMetrics"
          :hour-metrics="hourMetrics"
          :type-metrics="typeMetrics"
        />
      </pro-gate>
      <br />
      <spasm-list :spasms="spasms" :read-only="true" />
    </div>

    <br />
    <pro-gate description="Registre e consulte anotações clínicas sobre o paciente com o Plano Pro.">
      <doctor-notes :dependent-id="dependentId" @annotationsChanged="onAnnotationsChanged" />
    </pro-gate>

    <v-snackbar v-model="snackbar">{{ message }}</v-snackbar>
  </div>
</template>

<script>
import SpasmService from '../../lib/spasms-control/SpasmService'
import { PatientService } from '../../lib/spasms-control'
import { getToken, PAGES, setActualPage } from '../../utils'
import DependentCard from '../container/DependentCard.vue'
import SpasmList from '../container/SpasmList.vue'
import SpasmsChart from '../container/SpasmsChart.vue'
import SpasmsIndicators from '../container/SpasmsIndicators.vue'
import DoctorNotes from '../container/DoctorNotes.vue'
import ProGate from '../container/ProGate.vue'

export default {
  name: 'PagePatientDashboard',
  components: { DependentCard, SpasmList, SpasmsChart, SpasmsIndicators, DoctorNotes, ProGate },
  data() {
    return {
      patient: null,
      dependentId: null,
      spasms: [],
      metrics: {},
      periodMetrics: {},
      hourMetrics: {},
      typeMetrics: {},
      selectedDays: 7,
      chartAnnotations: [],
      snackbar: false,
      message: '',
      exporting: false
    }
  },
  watch: {
    selectedDays() {
      this.loadMetrics()
    }
  },
  methods: {
    loadPatient() {
      const service = new PatientService(getToken())
      service.get(this.dependentId).then(response => {
        this.patient = response.data
      }).catch(err => {
        console.error(err)
        this.$router.push('/patients')
      })
    },
    loadSpasms() {
      const service = new SpasmService(getToken())
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      service.list({ dependentId: this.dependentId, happenedAt: today.toJSON() }).then(response => {
        this.spasms = response.data
      }).catch(err => {
        console.error(err)
      })
    },
    loadMetrics() {
      const service = new SpasmService(getToken())
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const dateStr = today.toJSON()

      const p1 = service.getMetrics(this.dependentId, dateStr).then(r => { this.metrics = r.data })
      const p2 = service.getMetricsByPeriod(this.dependentId, dateStr).then(r => { this.periodMetrics = r.data })
      const p3 = service.getMetricsByHour(this.dependentId, dateStr).then(r => { this.hourMetrics = r.data })
      const p4 = service.getMetricsByType(this.dependentId, dateStr).then(r => { this.typeMetrics = r.data })

      return Promise.all([p1, p2, p3, p4])
    },
    onAnnotationsChanged(flaggedNotes) {
      const map = {}
      flaggedNotes.forEach(n => {
        const d = new Date(n.created_at)
        const date = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
        if (!map[date]) map[date] = []
        map[date].push(n.note)
      })
      this.chartAnnotations = Object.entries(map).map(([date, texts]) => ({
        date,
        text: texts.join('\n─────\n')
      }))
    },
    async exportPdf() {
      this.exporting = true
      try {
        const html2pdf = (await import('html2pdf.js')).default
        const element = document.getElementById('pdf-content')
        const patientName = this.patient ? this.patient.name.replace(/\s+/g, '-') : 'paciente'
        const date = new Date().toISOString().slice(0, 10)
        await html2pdf().set({
          margin: 10,
          filename: `relatorio-${patientName}-${date}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        }).from(element).save()
      } catch (err) {
        console.error(err)
        this.message = 'Erro ao gerar PDF'
        this.snackbar = true
      } finally {
        this.exporting = false
      }
    }
  },
  async mounted() {
    this.dependentId = parseInt(this.$route.params.id)
    this.loadPatient()
    this.loadMetrics()
    this.loadSpasms()
    setActualPage(PAGES.patients.id, this)
  }
}
</script>
